import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { appRoutes } from 'app/app-routes';
import ConfirmDialog, { ConfirmDialogProps } from 'common/components/ConfirmDialog';
import wsCookies from 'common/tools/ws-cookies';
import { useUsersDeleteMutation } from 'graphql/generated';
import moment from 'moment';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useNavigate } from 'react-router-dom';
import { cacheActions, cacheSelectors } from 'redux/cache.slice';
import { RootState } from 'redux/root-reducer';
import WsGridContainer from 'stories/layout/WsGridContainer';
import WsInfoFrame from 'stories/layout/WsInfoFrame';
import WsInfoFrameRow from 'stories/layout/WsInfoFrameRow';

const gridLR: [number, number] = [5, 7];

const UserInfo = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [deleteAccount] = useUsersDeleteMutation();
  const [showDeleteDlg, showDeleteDlgTo] = React.useState(false);
  const user = useSelector((state: RootState) => cacheSelectors.user(state));

  const deleteAction = async () => {
    showDeleteDlgTo(false);
    try {
      if (user) {
        navigate(appRoutes.home);

        await deleteAccount({
          variables: { id: user.id as string },
        });
      }
      dispatch(cacheActions.user(undefined));
      navigate(appRoutes.home);
      wsCookies.remove('userId');
      dispatch(
        cacheActions.addSnack({
          message: 'User deleted',
          variant: 'success',
        }),
      );
    } catch (error: any) {
      dispatch(
        cacheActions.addSnack({
          message: error.message,
          variant: 'error',
        }),
      );
    }
  };

  const deleteDialogProps: ConfirmDialogProps = {
    open: showDeleteDlg,
    title: 'Delete User',
    content: `Are you sure?`,
    ackLabel: 'Yes',
    ackAction: deleteAction,
    nackLabel: 'Cancel',
    nackAction: () => showDeleteDlgTo(false),
  };

  const handleDelete = () => {
    showDeleteDlgTo(true);
  };

  return (
    <WsGridContainer>
      <ConfirmDialog {...deleteDialogProps} />
      <WsInfoFrame width="60%" title="Personal Info" icon={<AccountCircleIcon color="secondary" />}>
        <WsInfoFrameRow gridLR={gridLR}>
          <Box>Email Address</Box>
          <Box>{user?.email}</Box>
        </WsInfoFrameRow>
        <WsInfoFrameRow gridLR={gridLR}>
          <Box>Name</Box>
          <Box>{user?.firstName + ' ' + user?.lastName}</Box>
        </WsInfoFrameRow>
        <WsInfoFrameRow gridLR={gridLR}>
          <Box>Zip Code</Box>
          <Box>{user?.zipCode}</Box>
        </WsInfoFrameRow>
        <WsInfoFrameRow gridLR={gridLR}>
          <Box>Date Account Created</Box>
          <Box>{moment(user?.dateAdded).format('dddd, MMMM Do YYYY, h:mm a')}</Box>
        </WsInfoFrameRow>
        <WsInfoFrameRow gridLR={gridLR}>
          <Box>Account Verified</Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {user?.verified ? 'Yes' : 'No'}
            {!user?.verified ? (
              <Box>
                <NavLink to={`${appRoutes.verifyEmail}/${user?.email}`}>
                  <Button
                    sx={{
                      marginLeft: 2,
                    }}
                    size="small"
                    color="primary"
                    variant="outlined"
                  >
                    Verify
                  </Button>
                </NavLink>
              </Box>
            ) : null}
          </Box>
        </WsInfoFrameRow>
        <WsInfoFrameRow gridLR={gridLR}>
          <Box>Actions</Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <NavLink to={appRoutes.changeUserInfo}>
              <Button color="primary" variant="outlined">
                Edit Info
              </Button>
            </NavLink>
            <Button
              sx={{
                marginLeft: 1,
              }}
              size="small"
              onClick={handleDelete}
            >
              Delete Account
            </Button>
          </Box>
        </WsInfoFrameRow>
      </WsInfoFrame>
    </WsGridContainer>
  );
};

export default UserInfo;
